/**
 * DhanStreamer — tick-by-tick MCX candle updates.
 *
 * Mirrors `binanceStreamer.js` but the ticks flow through OUR backend
 * (`/api/dhan/ws`) which proxies Dhan's `wss://api-feed.dhan.co`, keeping
 * the 24-h access token server-side. Downstream messages arrive as JSON:
 *   {s:"MCX_GOLD", p:1723.82, raw_inr:153485, usdinr:89.0, ts:…, seq:N}
 *
 * We share ONE relay socket for the whole app (all subscribed symbols
 * multiplexed) and hand out lightweight per-listener bar aggregators.
 */

// Tradelog internal ticker → routable via Dhan.
const DHAN_SYMBOLS = new Set(["MCX_GOLD", "MCX_SILVER", "MCX_CRUDEOIL"]);
// India Standard Time offset (+5:30) in seconds. MCX opens at 09:00 IST
// (03:30 UTC). We align intraday buckets to IST midnight rather than UTC :00
// so a 15m/60m candle starts on the IST clock — otherwise a UTC-aligned 60m
// bucket would put the MCX open in the middle of a candle.
const IST_OFFSET_SEC = 5 * 3600 + 30 * 60; // 19800

function normalizeTicker(sym) {
  if (!sym) return "";
  return String(sym).split(":").pop().toUpperCase();
}

export function hasDhanStream(symbol) {
  return DHAN_SYMBOLS.has(normalizeTicker(symbol));
}

function bucketSecondsFor(resolution) {
  const r = String(resolution);
  if (r === "D" || r === "1D") return 86400;
  if (r === "W" || r === "1W") return 86400 * 7;
  if (r === "M" || r === "1M") return 86400 * 30;
  return Math.max(60, parseInt(r, 10) * 60);
}

/* -------------------------------------------------------------------- *
 * Shared relay connection singleton. One WebSocket for the whole page
 * demux'd by symbol; every subscribeBars() adds a listener that filters
 * for its own ticker.
 * -------------------------------------------------------------------- */
class DhanRelay {
  constructor() {
    this.ws = null;
    this.state = "idle";               // "idle" | "connecting" | "open" | "closed"
    this.desiredSymbols = new Set();
    this.listeners = new Map();        // guid → { symbol, ccy, onTick }
    this.backoffMs = 1000;
    this.pendingSubs = new Set();
  }

  _baseUrl() {
    // REACT_APP_BACKEND_URL is set at build time — swap protocol to ws(s).
    const raw = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = new URL(raw);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/api/dhan/ws";
    url.search = "";
    return url.toString();
  }

  _ensure() {
    if (this.ws && (this.state === "open" || this.state === "connecting")) return;
    this.state = "connecting";
    try {
      this.ws = new WebSocket(this._baseUrl());
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.state = "open";
      this.backoffMs = 1000;
      // Re-subscribe everything we care about after (re)connect.
      if (this.desiredSymbols.size) {
        this.ws.send(JSON.stringify({
          action: "subscribe",
          symbols: [...this.desiredSymbols],
        }));
      }
    };
    this.ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch (_) { return; }
      if (!msg || !msg.s) return;
      // Fan out to every listener that cares about this symbol.
      for (const { symbol, onTick, ccy } of this.listeners.values()) {
        if (symbol !== msg.s) continue;
        // USD is the default price shown on the chart (we do the INR→USD
        // divide server-side). If the user switched the chart to INR via
        // the ccy picker, use the raw INR number instead.
        const price = ccy === "INR" ? msg.raw_inr : msg.p;
        onTick(price, msg.ts);
      }
    };
    this.ws.onclose = () => {
      this.state = "closed";
      this._scheduleReconnect();
    };
    this.ws.onerror = () => {
      try { this.ws && this.ws.close(); } catch (_) { /* ignore */ }
    };
  }

  _scheduleReconnect() {
    if (this.listeners.size === 0) return;   // no one wants ticks; sleep
    setTimeout(() => this._ensure(), this.backoffMs);
    this.backoffMs = Math.min(this.backoffMs * 2, 30000);
  }

  _syncSubs() {
    // Compute the union of listener symbols and diff against desiredSymbols.
    const wanted = new Set();
    for (const l of this.listeners.values()) wanted.add(l.symbol);
    const toAdd = [...wanted].filter(s => !this.desiredSymbols.has(s));
    const toRemove = [...this.desiredSymbols].filter(s => !wanted.has(s));
    this.desiredSymbols = wanted;
    if (this.state !== "open") return;
    if (toAdd.length) this.ws.send(JSON.stringify({ action: "subscribe", symbols: toAdd }));
    if (toRemove.length) this.ws.send(JSON.stringify({ action: "unsubscribe", symbols: toRemove }));
  }

  addListener(guid, symbol, ccy, onTick) {
    this.listeners.set(guid, { symbol, ccy, onTick });
    this._ensure();
    this._syncSubs();
  }

  removeListener(guid) {
    this.listeners.delete(guid);
    this._syncSubs();
    if (this.listeners.size === 0 && this.ws) {
      try { this.ws.close(); } catch (_) { /* ignore */ }
      this.ws = null;
      this.state = "idle";
    }
  }
}

const relay = new DhanRelay();

// Deterministic guid generator for live-price subscribers (separate from
// chart subscribeBars UIDs so the chart's bar aggregator and the order
// panel's price listener never step on each other).
let _priceGuidSeq = 0;

/**
 * Subscribe to the SAME live Dhan tick stream the MCX chart uses. Returns
 * an unsubscribe function. cb is called with (price: number). Non-MCX
 * symbols get a no-op unsub so callers don't have to branch.
 *
 * @param {string}  symbol   e.g. "MCX_GOLD"
 * @param {"USD"|"INR"} ccy  which price to report — must match what the
 *                           order panel is displaying, so the two never
 *                           disagree by a currency-conversion rate.
 * @param {(price:number)=>void} cb
 */
export function subscribeLivePrice(symbol, ccy, cb) {
  const ticker = String(symbol || "").toUpperCase();
  if (!DHAN_SYMBOLS.has(ticker)) return () => {};
  const guid = `dhan-price-${++_priceGuidSeq}`;
  relay.addListener(guid, ticker, ccy || "USD", (price) => {
    if (Number.isFinite(price)) { try { cb(price); } catch (_) {} }
  });
  return () => relay.removeListener(guid);
}

/**
 * Wrap a TradingView-UDF datafeed with a subscribeBars override that
 * uses the Dhan tick relay for MCX symbols. Non-MCX symbols delegate to
 * the base datafeed unchanged.
 *
 * @param baseDatafeed  the datafeed we're proxying
 * @param opts.getCcy   optional () → "USD" | "INR" — used so the chart
 *                      picks the right price from each tick payload
 */
export function wrapDatafeedWithDhanStream(baseDatafeed, opts = {}) {
  const getCcy = typeof opts.getCcy === "function" ? opts.getCcy : () => "USD";
  const subs = {}; // listenerGuid → { symbol, currentBar, bucketSec, onTick, ccy }

  return new Proxy(baseDatafeed, {
    get(target, prop) {
      if (prop === "subscribeBars") {
        return function subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeeded) {
          const ticker = normalizeTicker(symbolInfo?.ticker || symbolInfo?.name);
          if (!DHAN_SYMBOLS.has(ticker)) {
            return target.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeeded);
          }
          // Defensive: if TV is switching timeframe without unsubscribing
          // the old listener first (has happened on some TV builds during
          // rapid TF flips), drop any prior sub tied to this guid so we
          // never bucket ticks with the OLD resolution's bucketSec.
          if (subs[listenerGuid]) {
            try { relay.removeListener(listenerGuid); } catch (_) {}
            delete subs[listenerGuid];
          }
          const bucketSec = bucketSecondsFor(resolution);
          const sub = { symbol: ticker, bucketSec, onTick, currentBar: null, ccy: getCcy(), lastClose: null };
          subs[listenerGuid] = sub;
          // Tell TV to drop any bar it cached from a previous resolution
          // on this listener so the fresh bucketing below is what renders.
          if (typeof onResetCacheNeeded === "function") {
            try { onResetCacheNeeded(); } catch (_) {}
          }

          // Throttle to ~10 fps + drop identical-price no-op ticks. Live
          // feeds fire 3-30 times/sec; without this every tick forces TV
          // to re-render the current candle which felt "stuck / laggy"
          // during peak hours. 100 ms is well under human perception (~30
          // fps threshold) so the chart still animates smoothly.
          let lastPushMs = 0;
          const MIN_INTERVAL_MS = 100;
          const handleTick = (price, ts) => {
            if (!Number.isFinite(price)) return;
            // Guard against garbage timestamps from upstream — anything
            // outside a reasonable window falls back to now. Fixes a
            // subtle bug where an earlier ws relay was sending event-loop
            // monotonic time (~1987 in UNIX terms) that caused the "1-min
            // bar takes 6 min to close" glitch.
            const nowSec = Math.floor(Date.now() / 1000);
            const validTs = Number.isFinite(ts) && ts > 1577836800 && ts < nowSec + 3600
              ? ts : nowSec;
            const tradeMs = validTs * 1000;
            // TV UDF datafeed serializes bar time in **seconds** in both
            // /history and the subscribeBars onTick callback. Sending
            // milliseconds here (× 1000) made the widget treat streaming
            // ticks as a far-future bar it could never merge into the
            // current 1m bar — H/L accumulated over many minutes and the
            // countdown drifted off the true bar boundary.
            //
            // IST alignment: MCX bars must bucket on the IST clock (session
            // opens 09:00 IST = 03:30 UTC). Without the shift, 60m/D buckets
            // put the session open mid-candle. Shift into an IST-centred
            // second-count before flooring, then shift back.
            const secUtc = Math.floor(tradeMs / 1000);
            const secIst = secUtc + IST_OFFSET_SEC;
            const bucketIst = Math.floor(secIst / sub.bucketSec) * sub.bucketSec;
            const barTimeSec = bucketIst - IST_OFFSET_SEC;
            const newBar = !sub.currentBar || sub.currentBar.time !== barTimeSec;
            if (newBar) {
              if (sub.currentBar) {
                sub.lastClose = sub.currentBar.close;
                sub.onTick({ ...sub.currentBar });
              }
              // Point 3: seed the new bar's OPEN with the previous bar's
              // CLOSE so the candle series stays gap-less. Falls back to
              // the current tick price on the very first bar of a session.
              const seedOpen = sub.lastClose != null ? sub.lastClose : price;
              sub.currentBar = {
                time: barTimeSec,
                open: seedOpen,
                high: Math.max(seedOpen, price),
                low: Math.min(seedOpen, price),
                close: price,
                volume: 0,
              };
              lastPushMs = Date.now();
              sub.onTick({ ...sub.currentBar });
              return;
            }
            const prev = sub.currentBar.close;
            sub.currentBar.high = Math.max(sub.currentBar.high, price);
            sub.currentBar.low = Math.min(sub.currentBar.low, price);
            sub.currentBar.close = price;
            // Drop repeated ticks at the same price — nothing to render.
            if (price === prev) return;
            const nowMs = Date.now();
            if (nowMs - lastPushMs < MIN_INTERVAL_MS) return;
            lastPushMs = nowMs;
            sub.onTick({ ...sub.currentBar });
          };
          relay.addListener(listenerGuid, ticker, getCcy(), handleTick);
        };
      }

      if (prop === "unsubscribeBars") {
        return function unsubscribeBars(listenerGuid) {
          if (subs[listenerGuid]) {
            relay.removeListener(listenerGuid);
            delete subs[listenerGuid];
            return;
          }
          return target.unsubscribeBars(listenerGuid);
        };
      }

      const v = target[prop];
      return typeof v === "function" ? v.bind(target) : v;
    },
  });
}
